import { create } from 'zustand';

interface AuthState {
  accessToken: string | null;
  role: string | null;
  tenantId: string | null;
  cabinetId: string | null;
  userId: string | null;
  userName: string | null;
  // undefined = not yet fetched this session; string|null = resolved (presigned, short-lived)
  avatarUrl: string | null | undefined;
  // Tenant money/locale settings, used to format amounts. Defaults match a RO clinic until fetched.
  currency: string;
  locale: string;
  // Whether the clinic has AI features enabled (hides AI UI when off). True until fetched.
  aiEnabled: boolean;
  // The clinic country's standard VAT rate — the default for a new treatment. RO until fetched.
  defaultVatRate: number;
  // Feature flags on for this clinic. Empty until fetched, so a flagged nav entry appears once
  // known rather than flashing in and out.
  enabledFeatures: string[];
  isAuthenticated: boolean;

  setAuth: (token: string, role: string, tenantId: string | null) => void;
  setProfile: (name: string | null, avatarUrl: string | null) => void;
  setAvatarUrl: (url: string | null) => void;
  setTenantSettings: (currency: string, locale: string, aiEnabled: boolean, defaultVatRate: number, enabledFeatures: string[]) => void;
  clearAuth: () => void;
}

/** Pull the user id (sub), display name and active cabinet out of the JWT payload. */
function decodeToken(token: string): { userId: string | null; name: string | null; cabinetId: string | null } {
  try {
    const p = JSON.parse(atob(token.split('.')[1])) as Record<string, string>;
    return {
      userId:
        p.sub ??
        p['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ??
        p.nameid ??
        null,
      name: p.name ?? null,
      cabinetId: p.cabinet_id ? p.cabinet_id : null,
    };
  } catch {
    return { userId: null, name: null, cabinetId: null };
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  // Access token lives only in memory — never localStorage
  accessToken: null,
  role: null,
  tenantId: null,
  cabinetId: null,
  userId: null,
  userName: null,
  avatarUrl: undefined,
  currency: 'RON',
  locale: 'ro-RO',
  aiEnabled: true,
  defaultVatRate: 21,
  enabledFeatures: [],
  isAuthenticated: false,

  setAuth: (accessToken, role, tenantId) => {
    const { userId, name, cabinetId } = decodeToken(accessToken);
    set({ accessToken, role, tenantId, cabinetId, userId, userName: name, avatarUrl: undefined, isAuthenticated: true });
  },

  setProfile: (userName, avatarUrl) => set((s) => ({ userName: userName ?? s.userName, avatarUrl })),
  setAvatarUrl: (avatarUrl) => set({ avatarUrl }),
  setTenantSettings: (currency, locale, aiEnabled, defaultVatRate, enabledFeatures) =>
    set({ currency, locale, aiEnabled, defaultVatRate, enabledFeatures }),

  clearAuth: () =>
    set({ accessToken: null, role: null, tenantId: null, cabinetId: null, userId: null, userName: null, avatarUrl: undefined, isAuthenticated: false }),
}));
